import React, { useRef, useState } from 'react';
import axios from 'axios';
import { UploadCloud, Loader2, Trash2 } from 'lucide-react';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { resolveMediaUrl } from '../../lib/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared drag & drop (or click) image uploader for admin screens.
 * Posts to /api/storage/upload and hands the returned URL back to the caller.
 */
const ImageDropUpload = ({
  label,
  hint = '',
  value,
  onUploaded,
  folder,
  aspect = 'aspect-video',
  maxMb = 20,
  testId,
  objectFit = 'object-contain',
}) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image file.', variant: 'destructive' });
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      toast({ title: 'Too large', description: `Images must be ${maxMb}MB or smaller.`, variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      const { data } = await axios.post(`${API}/storage/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (!data?.url) throw new Error('Upload response missing URL');
      onUploaded(data.url);
      toast({ title: 'Uploaded', description: `${label || 'Image'} ready. Remember to save.` });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Could not upload the image.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <Label>{label}</Label>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`mt-1 relative ${aspect} w-full rounded-xl border-2 border-dashed cursor-pointer overflow-hidden flex items-center justify-center transition-colors ${
          dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400'
        }`}
        data-testid={testId}
      >
        {value ? (
          <img src={resolveMediaUrl(value)} alt={label || 'upload'} className={`w-full h-full ${objectFit} p-2`} />
        ) : (
          <div className="text-center px-3 py-6 text-gray-500">
            {uploading ? (
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-blue-600" />
            ) : (
              <>
                <UploadCloud className="w-7 h-7 mx-auto mb-2 text-gray-400" />
                <p className="text-sm font-medium">Drag &amp; drop or click</p>
                {hint && <p className="text-xs mt-1">{hint}</p>}
              </>
            )}
          </div>
        )}
        {uploading && value && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        )}
      </div>
      <input
        type="file"
        accept="image/*"
        ref={inputRef}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {value && (
        <button
          type="button"
          onClick={() => onUploaded('')}
          className="mt-2 text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1"
        >
          <Trash2 className="w-4 h-4" /> Remove
        </button>
      )}
    </div>
  );
};

export default ImageDropUpload;
